import { atom, useRecoilState } from "recoil";

import { Drawing } from "../lib/drawing";
import { read, write } from "../data/drawings";

const state = atom<Drawing[]>({
  key: "drawingsState",
  default: read(),
});

interface DrawingsHook {
  drawings: Drawing[];
  getDrawing: (id: string) => Drawing | undefined;
  update: (drawing: Drawing, newDrawing: Drawing) => void;
  create: () => void;
  copy: (drawing: Drawing) => void;
  insert: (
    toInsert: Drawing[],
    options?: { idx?: number; overwrite?: boolean }
  ) => void;
  remove: (drawing: Drawing) => void;
  removeAll: () => void;
}

export function useDrawings(): DrawingsHook {
  const [drawings, setDrawings] = useRecoilState(state);

  function toIdx(id: string): number {
    return drawings.findIndex((drawing) => drawing.id === id);
  }

  function getDrawing(id: string): Drawing | undefined {
    const idx = toIdx(id);
    return (idx < 0) ? undefined : drawings[idx];
  }

  function update(drawing: Drawing, updatedDrawing: Drawing): void {
    insert([updatedDrawing], { idx: toIdx(drawing.id), overwrite: true });
  }

  function create(): string {
    const drawing = new Drawing();
    insert([drawing]);
    return drawing.id;
  }

  function copy(drawing: Drawing): void {
    const original = drawings[toIdx(drawing.id)];
    insert([Drawing.from(original)]);
  }

  function insert(
    toInsert: Drawing[],
    options?: { idx?: number; overwrite?: boolean }
  ): void {
    const idx = options?.idx ?? 0;
    const overwrite = options?.overwrite ?? false;
    const next = [
      ...drawings.slice(0, idx),
      ...toInsert,
      ...drawings.slice(overwrite ? idx + toInsert.length : idx),
    ];
    setDrawings(next);
    write(next);
  }

  function remove(drawing: Drawing): void {
    const idx = toIdx(drawing.id);
    const next = [...drawings.slice(0, idx), ...drawings.slice(idx + 1)];
    setDrawings(next);
    write(next);
  }

  function removeAll(): void {
    setDrawings([]);
    write([]);
  }

  return {
    drawings,
    getDrawing,
    update,
    create,
    copy,
    insert,
    remove,
    removeAll,
  };
}
